/**
 * Utility functions for managing meta tags for social media sharing
 */

import { Counter, CustomButton } from '../types';

/**
 * Returns the canonical site origin to use for SEO-related URLs.
 * In production, force https://www.tapdonkey.com; otherwise use current origin.
 */
export const getPreferredOrigin = (): string => {
  try {
    const currentOrigin = window.location.origin;
    const isProdHost = /tapdonkey\.com$/i.test(window.location.hostname);
    // If already on www.tapdonkey.com, return as is
    if (window.location.hostname === 'www.tapdonkey.com') return currentOrigin;
    // If on apex tapdonkey.com or any subdomain under tapdonkey.com, normalize to www
    if (isProdHost) return 'https://www.tapdonkey.com';
    // Dev/preview environments keep their origin
    return currentOrigin;
  } catch (_) {
    return 'https://www.tapdonkey.com';
  }
};

// Define app-wide constants for consistent meta tag values
const APP_NAME = 'TapDonkey';
const DEFAULT_DESCRIPTION = 'Tap-to-vote counters with optional payments';
const SOCIAL_IMAGE_PATH = '/images/og.png';
const TWITTER_IMAGE_PATH = '/images/tw.png';

/**
 * Updates meta tags for a specific counter page to improve social media sharing
 * 
 * @param counter - The counter object whose data will be used for meta tags
 * @param buttons - Optional array of custom buttons to include in the description
 * @param baseUrl - Base URL of the application (defaults to window.location.origin)
 */
export const updateCounterMetaTags = (
  counter: Counter | undefined,
  buttons?: CustomButton[],
  baseUrl: string = getPreferredOrigin()
): void => {
  if (!counter) return;
  
  // Generate title with counter name
  const pageTitle = `${counter.name} - ${APP_NAME}`;
  
  // Generate a more specific description including counter details
  const pageDescription = buttons?.length 
    ? `Track ${counter.name} with ${buttons.length} custom counters. Create, share, and analyze your own interactive counters with TapDonkey.`
    : `Track and analyze ${counter.name} with TapDonkey. Create and share your own interactive counters for free.`;
  
  // Use consistent image for all social sharing
  const pageImage = `${baseUrl}${SOCIAL_IMAGE_PATH}`;
  const pageTwitterImage = `${baseUrl}${TWITTER_IMAGE_PATH}`;

  // Generate the canonical URL for this counter
  const pageUrl = `${baseUrl}/${counter._id}`;
  
  // Update meta tags
  updateMetaTags(pageTitle, pageDescription, pageImage, pageUrl, pageTwitterImage);
  
  // Add JSON-LD structured data for this counter
  addCounterStructuredData(counter, buttons, baseUrl);
};

/**
 * Helper function to update or create a specific meta tag
 * 
 * @param name - The name or property attribute of the meta tag
 * @param content - The content to set
 * @param attribute - Whether to use 'name' or 'property' attribute (defaults to 'name')
 */
export const updateMetaTag = (
  name: string,
  content: string,
  attribute: 'name' | 'property' = 'name'
): void => {
  // Try to find existing meta tag
  let metaTag = document.querySelector(`meta[${attribute}="${name}"]`);
  
  // Create tag if it doesn't exist
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute(attribute, name);
    document.head.appendChild(metaTag);
  }
  
  // Set content
  metaTag.setAttribute('content', content);
};

/**
 * Updates all meta tags with the provided values
 * 
 * @param title - The page title
 * @param description - The page description
 * @param image - The image URL
 * @param url - The page URL
 * @param twitterImage - Optional Twitter-specific image URL
 */
export const updateMetaTags = (
  title: string,
  description: string,
  image: string,
  url: string,
  twitterImage?: string
): void => {
  // Update document title
  document.title = title;
  
  // Update standard meta tags
  updateMetaTag('description', description);
  
  // Update Open Graph meta tags
  updateMetaTag('og:type', 'website', 'property');
  updateMetaTag('og:url', url, 'property');
  updateMetaTag('og:title', title, 'property');
  updateMetaTag('og:description', description, 'property');
  updateMetaTag('og:image', image, 'property');
  updateMetaTag('og:image:width', '1200', 'property');
  updateMetaTag('og:image:height', '630', 'property');
  updateMetaTag('og:image:alt', 'Site preview', 'property');
  
  // Update Twitter meta tags
  updateMetaTag('twitter:card', 'summary_large_image', 'property');
  updateMetaTag('twitter:url', url, 'property');
  updateMetaTag('twitter:title', title, 'property');
  updateMetaTag('twitter:description', description, 'property');
  updateMetaTag('twitter:image', twitterImage || image, 'property');
  
  // Add canonical URL link tag
  updateCanonicalUrl(url);
};

/**
 * Creates or updates the canonical URL link tag
 * 
 * @param url - The canonical URL to set
 */
export const updateCanonicalUrl = (url: string): void => {
  // Try to find existing canonical link tag
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  
  // Create tag if it doesn't exist
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  
  // Set href attribute
  canonicalLink.setAttribute('href', url);
};

/**
 * Adds JSON-LD structured data to the page
 * 
 * @param counter - The counter object to create structured data for
 * @param buttons - Optional array of custom buttons to include in the description
 * @param baseUrl - Base URL of the application
 */
export const addCounterStructuredData = (
  counter: Counter,
  buttons?: CustomButton[],
  baseUrl: string = getPreferredOrigin()
): void => {
  // Remove any existing JSON-LD script
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Create new structured data object
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": `${counter.name} - ${APP_NAME}`,
    "description": buttons?.length 
      ? `Track ${counter.name} with ${buttons.length} custom counters. Create, share, and analyze your own interactive counters with TapDonkey.`
      : `Track and analyze ${counter.name} with TapDonkey. Create and share your own interactive counters for free.`,
    "url": `${baseUrl}/${counter._id}`,
    "applicationCategory": "UtilityApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "operatingSystem": "Web browser"
  };
  
  // Create new script element
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(structuredData);
  
  // Add to document head
  document.head.appendChild(script);
};

/**
 * Adds site-wide JSON-LD structured data for the homepage
 */
export const addSiteStructuredData = (baseUrl: string = getPreferredOrigin()): void => {
  // Remove any existing JSON-LD script
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Create new structured data object
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": APP_NAME,
    "description": DEFAULT_DESCRIPTION,
    "url": baseUrl,
    "applicationCategory": "UtilityApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "operatingSystem": "Web browser",
    "screenshot": `${baseUrl}${SOCIAL_IMAGE_PATH}`,
    "featureList": "Create custom counters, Share with anyone, Track statistics, Real-time updates"
  };
  
  // Create new script element
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(structuredData);
  
  // Add to document head
  document.head.appendChild(script);
};

/**
 * Resets meta tags to default application values
 */
export const resetMetaTags = (): void => {
  const defaultUrl = getPreferredOrigin();
  const defaultImage = `${defaultUrl}${SOCIAL_IMAGE_PATH}`;
  const defaultTwitterImage = `${defaultUrl}${TWITTER_IMAGE_PATH}`;
  
  // Reset all meta tags to default values
  updateMetaTags(APP_NAME, DEFAULT_DESCRIPTION, defaultImage, defaultUrl, defaultTwitterImage);
  
  // Add site-wide structured data
  addSiteStructuredData(defaultUrl);
}; 